import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { PlusCircle, Edit2, Trash2, ChevronDown, ChevronRight, Save, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const ACCOUNT_TYPES = ['brokerage', 'cash', 'crypto', 'retirement', 'real_estate', 'other'];
const ASSET_CLASSES = ['stocks', 'bonds', 'crypto', 'cash', 'real_estate', 'other'];

const emptyAccount = { name: '', type: 'brokerage', institution: '', currency: 'USD', cash_balance: 0, manual_value: null, notes: '' };
const emptyHolding = { symbol: '', name: '', asset_class: 'stocks', quantity: 0, cost_basis: 0, last_price: 0, coin_id: '' };

const fmt = (n, currency = 'USD') =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(Number(n) || 0);

const WealthAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [holdingsByAccount, setHoldingsByAccount] = useState({});
  const [expanded, setExpanded] = useState({});
  const [newAccount, setNewAccount] = useState(null); // null | object
  const [editingAccount, setEditingAccount] = useState(null);
  const [newHolding, setNewHolding] = useState({}); // {accountId: holdingObj}

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/wealth/accounts`);
      setAccounts(r.data);
    } catch (e) {
      toast.error('Failed to load accounts');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const loadHoldings = async (accountId) => {
    try {
      const r = await axios.get(`${API}/wealth/accounts/${accountId}/holdings`);
      setHoldingsByAccount((prev) => ({ ...prev, [accountId]: r.data }));
    } catch (e) {
      toast.error('Failed to load holdings');
    }
  };

  const toggleExpand = async (accountId) => {
    const willOpen = !expanded[accountId];
    setExpanded((prev) => ({ ...prev, [accountId]: willOpen }));
    if (willOpen && !holdingsByAccount[accountId]) await loadHoldings(accountId);
  };

  const saveAccount = async () => {
    try {
      const payload = { ...newAccount };
      if (payload.manual_value === '' || payload.manual_value == null) delete payload.manual_value;
      await axios.post(`${API}/wealth/accounts`, payload);
      toast.success('Account added');
      setNewAccount(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add account');
    }
  };

  const saveAccountEdit = async () => {
    try {
      const payload = { ...editingAccount };
      const id = payload.id;
      delete payload.id;
      delete payload.user_id;
      delete payload.created_at;
      if (payload.manual_value === '' || payload.manual_value == null) delete payload.manual_value;
      await axios.put(`${API}/wealth/accounts/${id}`, payload);
      toast.success('Account updated');
      setEditingAccount(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update account');
    }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm('Delete this account and all its holdings?')) return;
    try {
      await axios.delete(`${API}/wealth/accounts/${id}`);
      toast.success('Account deleted');
      refresh();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const addHolding = async (accountId) => {
    const h = newHolding[accountId];
    if (!h || !h.symbol || h.quantity <= 0) {
      toast.error('Symbol and positive quantity required');
      return;
    }
    try {
      await axios.post(`${API}/wealth/accounts/${accountId}/holdings`, h);
      toast.success('Holding added');
      setNewHolding((prev) => ({ ...prev, [accountId]: null }));
      loadHoldings(accountId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add holding');
    }
  };

  const deleteHolding = async (accountId, holdingId) => {
    if (!window.confirm('Delete this holding?')) return;
    try {
      await axios.delete(`${API}/wealth/holdings/${holdingId}`);
      toast.success('Holding deleted');
      loadHoldings(accountId);
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="p-8 md:p-12" data-testid="wealth-accounts-page">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-chivo font-bold text-slate-900 tracking-tight">Accounts</h1>
            <p className="text-slate-600 mt-1 font-ibm">Brokerage, cash, crypto, retirement and real estate.</p>
          </div>
          {!newAccount && (
            <Button
              onClick={() => setNewAccount({ ...emptyAccount })}
              className="bg-slate-900 text-white rounded-sm hover:bg-slate-800"
              data-testid="new-account-btn"
            >
              <PlusCircle size={14} className="mr-2" />
              New account
            </Button>
          )}
        </div>

        {/* NEW ACCOUNT CARD */}
        {newAccount && (
          <div className="mb-6 border border-slate-200 bg-slate-50 p-6 rounded-sm" data-testid="new-account-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Input
                placeholder="Account name (e.g. Fidelity IRA)"
                value={newAccount.name}
                onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                data-testid="new-account-name"
              />
              <Select value={newAccount.type} onValueChange={(v) => setNewAccount({ ...newAccount, type: v })}>
                <SelectTrigger data-testid="new-account-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                placeholder="Institution"
                value={newAccount.institution || ''}
                onChange={(e) => setNewAccount({ ...newAccount, institution: e.target.value })}
                data-testid="new-account-institution"
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Cash balance"
                value={newAccount.cash_balance}
                onChange={(e) => setNewAccount({ ...newAccount, cash_balance: parseFloat(e.target.value) || 0 })}
                data-testid="new-account-cash"
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Manual value (real estate etc.)"
                value={newAccount.manual_value ?? ''}
                onChange={(e) => setNewAccount({ ...newAccount, manual_value: e.target.value === '' ? null : parseFloat(e.target.value) })}
                data-testid="new-account-manual"
              />
              <Input
                placeholder="Currency"
                maxLength={3}
                value={newAccount.currency}
                onChange={(e) => setNewAccount({ ...newAccount, currency: e.target.value.toUpperCase() })}
                data-testid="new-account-currency"
              />
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Button onClick={saveAccount} className="bg-slate-900 text-white rounded-sm" data-testid="save-account-btn">
                <Save size={14} className="mr-2" /> Save
              </Button>
              <Button variant="outline" onClick={() => setNewAccount(null)} className="rounded-sm" data-testid="cancel-account-btn">
                <X size={14} className="mr-2" /> Cancel
              </Button>
            </div>
          </div>
        )}

        {/* ACCOUNTS LIST */}
        {accounts.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-sm p-12 text-center" data-testid="no-accounts">
            <p className="font-ibm text-slate-600">No accounts yet. Add your first one above.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((a) => {
              const isEditing = editingAccount?.id === a.id;
              const isOpen = !!expanded[a.id];
              const hs = holdingsByAccount[a.id] || [];
              const nh = newHolding[a.id];
              return (
                <div key={a.id} className="border border-slate-200 rounded-sm bg-white" data-testid={`account-row-${a.id}`}>
                  <div className="flex items-center justify-between p-4">
                    <button onClick={() => toggleExpand(a.id)} className="flex items-center gap-3 flex-1 text-left" data-testid={`toggle-account-${a.id}`}>
                      {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      <div>
                        <p className="font-ibm font-semibold text-slate-900">{a.name}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-widest font-ibm mt-0.5">
                          {a.type.replace('_', ' ')}{a.institution ? ` · ${a.institution}` : ''}
                        </p>
                      </div>
                    </button>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditingAccount({ ...a })} data-testid={`edit-account-${a.id}`}>
                        <Edit2 size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteAccount(a.id)} data-testid={`delete-account-${a.id}`}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50" data-testid={`edit-account-form-${a.id}`}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Input value={editingAccount.name} onChange={(e) => setEditingAccount({ ...editingAccount, name: e.target.value })} />
                        <Select value={editingAccount.type} onValueChange={(v) => setEditingAccount({ ...editingAccount, type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input placeholder="Institution" value={editingAccount.institution || ''} onChange={(e) => setEditingAccount({ ...editingAccount, institution: e.target.value })} />
                        <Input type="number" placeholder="Cash balance" value={editingAccount.cash_balance} onChange={(e) => setEditingAccount({ ...editingAccount, cash_balance: parseFloat(e.target.value) || 0 })} />
                        <Input type="number" placeholder="Manual value" value={editingAccount.manual_value ?? ''} onChange={(e) => setEditingAccount({ ...editingAccount, manual_value: e.target.value === '' ? null : parseFloat(e.target.value) })} />
                        <Input value={editingAccount.currency} onChange={(e) => setEditingAccount({ ...editingAccount, currency: e.target.value.toUpperCase() })} />
                      </div>
                      <div className="flex items-center gap-2 mt-3">
                        <Button onClick={saveAccountEdit} className="bg-slate-900 text-white rounded-sm" data-testid={`save-edit-account-${a.id}`}>Save</Button>
                        <Button variant="outline" onClick={() => setEditingAccount(null)} className="rounded-sm">Cancel</Button>
                      </div>
                    </div>
                  )}

                  {isOpen && !isEditing && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50/50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-ibm font-semibold uppercase tracking-widest text-slate-600">
                          Holdings ({hs.length})
                        </h4>
                        {!nh && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setNewHolding((prev) => ({ ...prev, [a.id]: { ...emptyHolding } }))}
                            className="rounded-sm"
                            data-testid={`add-holding-btn-${a.id}`}
                          >
                            <PlusCircle size={12} className="mr-1" /> Add holding
                          </Button>
                        )}
                      </div>

                      {hs.length === 0 && !nh && (
                        <p className="text-xs text-slate-500 font-ibm py-2">No holdings in this account yet.</p>
                      )}

                      {hs.length > 0 && (
                        <table className="w-full text-sm font-ibm" data-testid={`holdings-table-${a.id}`}>
                          <thead>
                            <tr className="text-xs text-slate-500 uppercase tracking-widest">
                              <th className="text-left py-2">Symbol</th>
                              <th className="text-left py-2">Class</th>
                              <th className="text-right py-2">Qty</th>
                              <th className="text-right py-2">Price</th>
                              <th className="text-right py-2">Value</th>
                              <th className="text-right py-2">P&amp;L</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {hs.map((h) => (
                              <tr key={h.id} className="border-t border-slate-100" data-testid={`holding-row-${h.id}`}>
                                <td className="py-2 font-semibold text-slate-900">{h.symbol}</td>
                                <td className="py-2 capitalize text-slate-600">{h.asset_class.replace('_', ' ')}</td>
                                <td className="py-2 text-right tabular-nums">{h.quantity}</td>
                                <td className="py-2 text-right tabular-nums">{fmt(h.last_price)}</td>
                                <td className="py-2 text-right tabular-nums font-semibold">{fmt(h.market_value)}</td>
                                <td className={`py-2 text-right tabular-nums font-semibold ${h.unrealized_pnl > 0 ? 'text-emerald-600' : h.unrealized_pnl < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                  {h.unrealized_pnl > 0 ? '+' : ''}{fmt(h.unrealized_pnl)}
                                </td>
                                <td className="py-2 text-right">
                                  <Button size="sm" variant="ghost" onClick={() => deleteHolding(a.id, h.id)} data-testid={`delete-holding-${h.id}`}>
                                    <Trash2 size={12} className="text-red-600" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}

                      {nh && (
                        <div className="mt-3 p-3 border border-slate-200 bg-white rounded-sm grid grid-cols-1 md:grid-cols-6 gap-2 items-center" data-testid={`new-holding-form-${a.id}`}>
                          <Input placeholder="Symbol (AAPL/bitcoin)" value={nh.symbol} onChange={(e) => setNewHolding((p) => ({ ...p, [a.id]: { ...nh, symbol: e.target.value } }))} data-testid={`holding-symbol-${a.id}`} />
                          <Select value={nh.asset_class} onValueChange={(v) => setNewHolding((p) => ({ ...p, [a.id]: { ...nh, asset_class: v } }))}>
                            <SelectTrigger data-testid={`holding-class-${a.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{ASSET_CLASSES.map((c) => <SelectItem key={c} value={c}>{c.replace('_', ' ')}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input type="number" step="0.0001" placeholder="Quantity" value={nh.quantity} onChange={(e) => setNewHolding((p) => ({ ...p, [a.id]: { ...nh, quantity: parseFloat(e.target.value) || 0 } }))} data-testid={`holding-qty-${a.id}`} />
                          <Input type="number" step="0.01" placeholder="Cost basis" value={nh.cost_basis} onChange={(e) => setNewHolding((p) => ({ ...p, [a.id]: { ...nh, cost_basis: parseFloat(e.target.value) || 0 } }))} data-testid={`holding-cost-${a.id}`} />
                          <Input type="number" step="0.01" placeholder="Last price" value={nh.last_price} onChange={(e) => setNewHolding((p) => ({ ...p, [a.id]: { ...nh, last_price: parseFloat(e.target.value) || 0 } }))} data-testid={`holding-price-${a.id}`} />
                          <div className="flex gap-1">
                            <Button size="sm" onClick={() => addHolding(a.id)} className="bg-slate-900 text-white rounded-sm" data-testid={`save-holding-${a.id}`}>Save</Button>
                            <Button size="sm" variant="outline" onClick={() => setNewHolding((p) => ({ ...p, [a.id]: null }))} className="rounded-sm">X</Button>
                          </div>
                          {nh.asset_class === 'crypto' && (
                            <div className="md:col-span-6 text-xs font-ibm text-slate-500">
                              For crypto, use the CoinGecko coin id as symbol (e.g. <code className="bg-slate-100 px-1">bitcoin</code>, <code className="bg-slate-100 px-1">ethereum</code>) so live refresh works.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default WealthAccounts;
